import { useState } from "react";
import { Pencil, Tags } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { PatientTagInput } from "../../PatientTagInput";
import { CartaoDados } from "./CartaoDados";
import { LinhaEditavel } from "./LinhaEditavel";
import type { SecaoProps } from "./tipos";
import { useCamposDaFicha } from "./use-campos-da-ficha";

const botaoPrimario = "inline-flex h-7 items-center rounded-md bg-brand-600 px-2.5 text-[11.5px] font-semibold text-white hover:bg-brand-700 disabled:opacity-60";
const botaoNeutro =
  "inline-flex h-7 items-center rounded-md border border-zinc-200 px-2.5 text-[11.5px] font-medium text-zinc-600 hover:bg-zinc-50 dark:border-border dark:text-muted-foreground dark:hover:bg-muted";

export function SecaoComplementar({ patient, salvar, somenteLeitura }: SecaoProps) {
  const comum = { somenteLeitura };
  const { ver, obrigatorio } = useCamposDaFicha();
  return (
    <CartaoDados icon={Tags} titulo="Tags, plano e observações" subtitulo="Informações complementares" tom="ambar">
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <LinhaDeTags tags={patient.tags ?? []} onSave={salvar.tags} somenteLeitura={somenteLeitura} />
          {ver("convenio") && (
            <>
              <LinhaEditavel {...comum} obrigatorio={obrigatorio("convenio")} label="Plano de saúde" value={patient.insurancePlanName ?? ""} onSave={salvar.texto("insurancePlanName", "Plano")} />
              <LinhaEditavel {...comum} label="Número do plano" value={patient.insurancePlanNumber ?? ""} onSave={salvar.texto("insurancePlanNumber", "Nº do plano")} />
            </>
          )}
        </div>
        <Observacoes valor={patient.notes ?? ""} onSave={salvar.texto("notes", "Observações")} somenteLeitura={somenteLeitura} />
      </div>
    </CartaoDados>
  );
}

function LinhaDeTags({ tags, onSave, somenteLeitura }: { tags: string[]; onSave: (t: string[]) => Promise<void>; somenteLeitura: boolean }) {
  const sugestoes = trpc.patient.listTags.useQuery(undefined, { enabled: !somenteLeitura });
  const [editando, setEditando] = useState(false);
  const [rascunho, setRascunho] = useState(tags);
  const [salvando, setSalvando] = useState(false);

  if (editando) {
    return (
      <div className="rounded-md bg-brand-50/60 p-2.5 ring-1 ring-brand-300 dark:bg-primary/10 dark:ring-primary/40">
        <p className="mb-1.5 text-[12.5px] text-zinc-500 dark:text-muted-foreground">Tags</p>
        <PatientTagInput value={rascunho} onChange={setRascunho} suggestions={sugestoes.data ?? []} />
        <div className="mt-2 flex items-center gap-2">
          <button
            type="button"
            disabled={salvando}
            className={botaoPrimario}
            onClick={async () => {
              setSalvando(true);
              try {
                await onSave(rascunho);
                setEditando(false);
              } finally {
                setSalvando(false);
              }
            }}
          >
            {salvando ? "Salvando..." : "Salvar tags"}
          </button>
          <button type="button" disabled={salvando} className={botaoNeutro} onClick={() => setEditando(false)}>
            Cancelar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      role={somenteLeitura ? undefined : "button"}
      tabIndex={somenteLeitura ? undefined : 0}
      aria-label={somenteLeitura ? undefined : "Editar Tags"}
      onClick={() => {
        if (somenteLeitura) return;
        setRascunho(tags);
        setEditando(true);
      }}
      className={
        "group flex items-start gap-3 rounded-md px-2.5 py-1.5" +
        (somenteLeitura ? "" : " cursor-pointer transition-colors hover:bg-zinc-50 dark:hover:bg-muted/40")
      }
    >
      <span className="w-[42%] shrink-0 text-[12.5px] text-zinc-500 dark:text-muted-foreground">Tags</span>
      <div className="flex min-w-0 flex-1 flex-wrap gap-1">
        {tags.length ? (
          tags.map((t) => (
            <span key={t} className="rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-medium text-brand-700 ring-1 ring-brand-200 dark:bg-primary/15 dark:text-primary dark:ring-primary/30">
              {t}
            </span>
          ))
        ) : (
          <span className="text-[13px] font-medium text-zinc-300 dark:text-muted-foreground/50">—</span>
        )}
      </div>
      {!somenteLeitura && <Pencil aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0 text-zinc-300 transition-colors group-hover:text-brand-600 dark:text-muted-foreground/50" />}
    </div>
  );
}

/** Observações numa caixa própria: texto longo não cabe no formato rótulo/valor. */
function Observacoes({ valor, onSave, somenteLeitura }: { valor: string; onSave: (v: string) => Promise<void>; somenteLeitura: boolean }) {
  const [editando, setEditando] = useState(false);
  const [rascunho, setRascunho] = useState(valor);
  const [salvando, setSalvando] = useState(false);

  const salvar = async () => {
    if (rascunho.trim() === valor.trim()) return setEditando(false);
    setSalvando(true);
    try {
      await onSave(rascunho);
      setEditando(false);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="flex min-h-[132px] flex-col rounded-lg border border-zinc-200 bg-zinc-50/60 p-3 dark:border-border dark:bg-muted/20">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-[12.5px] text-zinc-500 dark:text-muted-foreground">Observações</span>
        {!somenteLeitura && !editando && (
          <button
            type="button"
            aria-label="Editar Observações"
            onClick={() => {
              setRascunho(valor);
              setEditando(true);
            }}
            className="flex h-6 w-6 items-center justify-center rounded-md text-zinc-400 hover:bg-white hover:text-brand-600 dark:hover:bg-muted"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {editando ? (
        <>
          <textarea
            autoFocus
            value={rascunho}
            onChange={(e) => setRascunho(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setEditando(false);
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) salvar();
            }}
            rows={5}
            disabled={salvando}
            placeholder="Notas clínicas, preferências..."
            className="w-full flex-1 resize-none rounded-md border border-brand-300 bg-white p-2 text-[13px] text-zinc-900 outline-none dark:border-primary/40 dark:bg-card dark:text-foreground"
          />
          <div className="mt-2 flex items-center gap-2">
            <button type="button" disabled={salvando} onClick={salvar} className={botaoPrimario}>
              {salvando ? "Salvando..." : "Salvar"}
            </button>
            <button type="button" disabled={salvando} onClick={() => setEditando(false)} className={botaoNeutro}>
              Cancelar
            </button>
          </div>
        </>
      ) : valor ? (
        <p className="whitespace-pre-wrap break-words text-[13px] text-zinc-800 dark:text-foreground">{valor}</p>
      ) : (
        <p className="text-[13px] text-zinc-400 dark:text-muted-foreground">Nenhuma observação.</p>
      )}
    </div>
  );
}
